import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Buildings, MapPin, ShieldCheck, FlagBanner, Database, Lock, Handshake } from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";
import { COMPANY, ATIKXZIO_SITE_URL, CMH_NAME } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { useAutoPlay } from "../lib/useAutoPlay";
import { Reveal } from "../components/blocks";
import { ScreenShot, SCREEN_CAPTION } from "../components/ProductMock";
import { CmhHeroVisual } from "../components/replicas/CmhReplicas";
import { Leadership } from "../components/Leadership";
import { Journey } from "../components/Journey";

const STR = {
  en: {
    heroEyebrow: "Priyajit Systems · Healthcare & people software, made in India",
    h1a: "Software that makes care ",
    h1b: "run better.",
    heroSub: "We build Atikxzio — products that help Indian hospitals run every bed and bill, and help people make better career and life decisions.",
    heroProducts: "Explore our products",
    heroTalk: "Talk to us",
    tabHms: "Atikxzio HMS · For hospitals",
    tabHmsShort: "HMS · Hospitals",
    tabCmh: `${CMH_NAME} · Launching soon`,
    tabCmhShort: "CMH · Soon",
    hmsStageLine: "The complete hospital system, from booking to discharge",
    hmsStageCta: "Visit Atikxzio",
    cmhStageLine: "Career guidance and well-being, with AI and real counsellors",
    cmhStageCta: "Join the early list",
    trust: [
      { icon: FlagBanner, t: "Made in India" },
      { icon: Database, t: "Data stored in India" },
      { icon: Lock, t: "DPDP Act aligned" },
      { icon: Handshake, t: "Founder-led support" },
    ],
    whatEyebrow: "What we do",
    what: [
      { icon: Buildings, t: "We build products", d: "Healthcare software used every day by real staff — reception, doctors, nurses, cashiers and owners." },
      { icon: MapPin, t: "Built where care happens", d: "Designed inside Priyajit Ortho Neuro Rehabilitation Center, Gorakhpur, not from a distance." },
      { icon: ShieldCheck, t: "Engineered to last", d: "Secure, audited, exportable. Your data stays yours, with no lock-in." },
    ],
    productsEyebrow: "Our products",
    productsTitle: "One company, two products",
    hmsFlag: "Flagship",
    hmsLine: "The complete hospital system, from booking to discharge.",
    hmsVisit: "Visit Atikxzio",
    hmsPricing: "Pricing",
    cmhFlag: "Launching soon",
    cmhLine: "Career clarity and mental well-being in one place.",
    cmhCta: "Learn more",
    journeyMore: "See everything Atikxzio does",
    numbersEyebrow: "By the numbers",
    numbers: [
      ["15+", "years combined experience"],
      ["1000+", "patients treated at our own centre"],
      ["100%", "data in India"],
      ["1 day", "to train your staff"],
    ],
    careersEyebrow: "Careers",
    careersTitle: "We're hiring — 9 roles, Noida or remote",
    careersRoles: ["Customer Success Lead", "Business Development", "Sales Executive"],
    careersCta: "View openings",
    ctaTitle: "Building for a hospital, clinic or health network? Let's talk.",
    ctaDemo: "Book a demo",
    ctaContact: "Contact",
  },
  hi: {
    heroEyebrow: "प्रियाजित सिस्टम्स · हेल्थकेयर और लोगों के लिए सॉफ्टवेयर, भारत में निर्मित",
    h1a: "ऐसा सॉफ्टवेयर जो देखभाल को ",
    h1b: "बेहतर चलाए।",
    heroSub: "हम Atikxzio बनाते हैं — ऐसे प्रोडक्ट जो भारतीय अस्पतालों का हर बेड और बिल संभालें, और लोगों को करियर व जीवन के बेहतर फ़ैसले लेने में मदद करें।",
    heroProducts: "हमारे प्रोडक्ट देखें",
    heroTalk: "हमसे बात करें",
    tabHms: "Atikxzio HMS · अस्पतालों के लिए",
    tabHmsShort: "HMS · अस्पताल",
    tabCmh: `${CMH_NAME} · जल्द लॉन्च`,
    tabCmhShort: "CMH · जल्द",
    hmsStageLine: "बुकिंग से डिस्चार्ज तक, पूरा अस्पताल सिस्टम",
    hmsStageCta: "Atikxzio पर जाएँ",
    cmhStageLine: "AI और असली काउंसलर्स के साथ करियर मार्गदर्शन व मानसिक स्वास्थ्य",
    cmhStageCta: "अर्ली लिस्ट में जुड़ें",
    trust: [
      { icon: FlagBanner, t: "मेड इन इंडिया" },
      { icon: Database, t: "डेटा भारत में संग्रहित" },
      { icon: Lock, t: "DPDP एक्ट के अनुरूप" },
      { icon: Handshake, t: "फ़ाउंडर-नेतृत्व सपोर्ट" },
    ],
    whatEyebrow: "हम क्या करते हैं",
    what: [
      { icon: Buildings, t: "हम प्रोडक्ट बनाते हैं", d: "ऐसा हेल्थकेयर सॉफ्टवेयर जिसे असली स्टाफ़ रोज़ चलाता है — रिसेप्शन, डॉक्टर, नर्स, कैशियर और मालिक।" },
      { icon: MapPin, t: "वहीं बना, जहाँ इलाज होता है", d: "प्रियाजित ऑर्थो न्यूरो रिहैबिलिटेशन सेंटर, गोरखपुर के अंदर डिज़ाइन किया गया — दूर बैठकर नहीं।" },
      { icon: ShieldCheck, t: "टिकने के लिए बना", d: "सुरक्षित, ऑडिटेड, एक्सपोर्ट योग्य। डेटा आपका ही रहता है, कोई लॉक-इन नहीं।" },
    ],
    productsEyebrow: "हमारे प्रोडक्ट",
    productsTitle: "एक कंपनी, दो प्रोडक्ट",
    hmsFlag: "प्रमुख प्रोडक्ट",
    hmsLine: "बुकिंग से डिस्चार्ज तक, पूरा अस्पताल सिस्टम।",
    hmsVisit: "Atikxzio पर जाएँ",
    hmsPricing: "प्राइसिंग",
    cmhFlag: "जल्द लॉन्च",
    cmhLine: "करियर की स्पष्टता और मानसिक स्वास्थ्य, एक ही जगह।",
    cmhCta: "और जानें",
    journeyMore: "Atikxzio क्या-क्या करता है, देखें",
    numbersEyebrow: "आँकड़ों में",
    numbers: [
      ["15+", "वर्षों का संयुक्त अनुभव"],
      ["1000+", "मरीज़ हमारे अपने सेंटर पर"],
      ["100%", "डेटा भारत में"],
      ["1 दिन", "में स्टाफ़ ट्रेनिंग"],
    ],
    careersEyebrow: "करियर",
    careersTitle: "हम हायर कर रहे हैं — 9 रोल, नोएडा या रिमोट",
    careersRoles: ["Customer Success Lead", "Business Development", "Sales Executive"],
    careersCta: "ओपनिंग देखें",
    ctaTitle: "किसी अस्पताल, क्लिनिक या हेल्थ नेटवर्क के लिए बना रहे हैं? बात करते हैं।",
    ctaDemo: "डेमो बुक करें",
    ctaContact: "संपर्क",
  },
};

const Eyebrow = ({ children, onDark }) => (
  <p className={`text-xs font-bold uppercase tracking-[0.22em] ${onDark ? "text-electric-light" : "text-electric-ink"}`}>{children}</p>
);

/* Hero product stage: two tabs, one tilted browser frame, auto-switching. */
const ProductStage = ({ s, lang }) => {
  const { ref, active, pick, mounted } = useAutoPlay(2, { first: 6000, every: 6000, hold: 10000 });
  const tab = active === 0 ? "hms" : "cmh";

  const isHms = tab === "hms";

  return (
    <div ref={ref} className="mt-14 lg:mt-16" data-testid="hero-product-stage">
      <div className="mx-auto flex w-full max-w-md rounded-full border border-white/15 bg-white/[0.06] p-1 sm:w-fit sm:max-w-none" role="tablist">
        {[["hms", s.tabHms, s.tabHmsShort], ["cmh", s.tabCmh, s.tabCmhShort]].map(([key, label, short]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            onClick={() => pick(key === "hms" ? 0 : 1)}
            className={`flex-1 whitespace-nowrap rounded-full px-3 py-2 text-[11px] font-bold transition-colors duration-300 sm:flex-none sm:px-6 sm:py-2.5 sm:text-xs ${
              tab === key ? "bg-white text-navy" : "text-white/70 hover:text-white"
            }`}
            data-testid={`hero-tab-${key}`}
          >
            <span className="sm:hidden">{short}</span>
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="group relative mx-auto mt-9 max-w-[62rem]" style={{ perspective: "1600px" }}>
        <div aria-hidden className="absolute inset-x-8 bottom-0 h-44 rounded-[50%] bg-electric/30 blur-[90px]" />
        <div className="relative max-h-[22rem] overflow-hidden sm:max-h-[26rem] lg:max-h-[30rem]">
          <div className="origin-top transition-transform duration-500 ease-out lg:[transform:rotateX(8deg)] lg:group-hover:[transform:rotateX(0deg)] motion-reduce:!transform-none">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={tab}
                initial={mounted ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
              >
                {isHms ? (
                  <ScreenShot
                    src="/screens/owner-dashboard.webp"
                    alt="Atikxzio HMS owner's dashboard showing money collected, billed revenue, bed occupancy and dues"
                    label="atikxzio.com/dashboard"
                    lang={lang}
                    priority
                    caption={false}
                  />
                ) : (
                  <div className="mx-auto max-w-[34rem] sm:max-w-[38rem] lg:max-w-[42rem]">
                    <CmhHeroVisual lang={lang} />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#071433] to-transparent" />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-center">
        <span className="text-sm text-slate-300">{isHms ? s.hmsStageLine : s.cmhStageLine}</span>
        {isHms ? (
          <a href={ATIKXZIO_SITE_URL} target="_top" rel="noopener" className="group/link inline-flex items-center gap-1.5 text-sm font-bold text-electric-light" data-testid="hero-stage-hms-cta">
            {s.hmsStageCta} <ArrowRight size={14} className="transition-transform duration-200 group-hover/link:translate-x-1" />
          </a>
        ) : (
          <Link to="/products/atikxzio-cmh" className="group/link inline-flex items-center gap-1.5 text-sm font-bold text-[#8FC9BF]" data-testid="hero-stage-cmh-cta">
            {s.cmhStageCta} <ArrowRight size={14} className="transition-transform duration-200 group-hover/link:translate-x-1" />
          </Link>
        )}
      </div>
    </div>
  );
};

export default function Home() {
  const { lang } = useLang();
  const s = STR[lang];

  useSeo({
    title: "Priyajit Systems — Healthcare software company | Makers of Atikxzio",
    description: "Priyajit Systems Private Limited is a healthcare software company from Uttar Pradesh and the maker of Atikxzio HMS — built inside a working hospital, with your data kept in India.",
    image: "/og-image.jpg",
    keywords: "healthcare software company India, Priyajit Systems, Atikxzio, hospital software Uttar Pradesh, Noida healthcare product company",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: COMPANY.legal,
      url: "https://www.priyajitsystems.in",
      logo: "https://www.priyajitsystems.in/logo.png",
      email: COMPANY.email,
      telephone: COMPANY.phone,
      address: { "@type": "PostalAddress", streetAddress: COMPANY.address, addressCountry: "IN" },
      makesOffer: [{ "@type": "Offer", itemOffered: { "@type": "SoftwareApplication", name: "Atikxzio HMS", applicationCategory: "HealthApplication", url: ATIKXZIO_SITE_URL } }],
    },
  });

  return (
    <div className="bg-white">
      {/* 1 · Hero */}
      <section className="relative overflow-hidden bg-mesh" data-testid="hero-section">
        <div aria-hidden className="absolute inset-0 bg-dots opacity-40" />
        <div className="relative mx-auto max-w-[1200px] px-5 pt-36 lg:px-8 lg:pt-44">
          <div className="mx-auto max-w-[55rem] text-center">
            <motion.span
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="inline-flex items-center rounded-full border border-white/20 bg-white/[0.07] px-4 py-1.5 text-[11px] font-bold tracking-[0.08em] text-white/85"
              data-testid="hero-company-chip"
            >
              {s.heroEyebrow}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.1 }}
              className="mt-7 font-display text-[2.5rem] font-black leading-[1.08] tracking-[-0.02em] text-white sm:text-5xl lg:text-[4rem]"
              data-testid="hero-h1"
            >
              {s.h1a}
              <span className="bg-gradient-to-r from-electric-light to-teal bg-clip-text text-transparent">{s.h1b}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.26 }}
              className="mx-auto mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-[17px]"
            >
              {s.heroSub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.38 }}
              className="mt-10 flex flex-wrap justify-center gap-3"
            >
              <a href="#products" className="group inline-flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-bold text-white transition-transform duration-200 hover:-translate-y-0.5" data-testid="hero-explore-products">
                {s.heroProducts} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-white/35 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="hero-talk-to-us">
                {s.heroTalk}
              </Link>
            </motion.div>
          </div>

          <ProductStage s={s} lang={lang} />
        </div>

        {/* 2 · Trust strip */}
        <div className="relative border-t border-white/10 bg-[#071433]/80 backdrop-blur-sm" data-testid="home-trust-strip">
          <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-x-8 gap-y-2.5 px-5 py-5 lg:justify-start lg:px-8">
            {s.trust.map((item) => (
              <span key={item.t} className="flex items-center gap-2 text-[13px] font-semibold text-white/80">
                <item.icon size={16} weight="duotone" className="text-teal" /> {item.t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · What we do */}
      <section className="bg-[#F6F8FC] py-20 lg:py-32" data-testid="home-what-we-do">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal><Eyebrow>{s.whatEyebrow}</Eyebrow></Reveal>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {s.what.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.08}>
                <div className="h-full rounded-[20px] border border-navy/[0.08] bg-white p-7" data-testid={`home-what-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-white"><w.icon size={23} weight="duotone" /></span>
                  <h2 className="mt-5 font-display text-lg font-bold tracking-[-0.01em] text-navy">{w.t}</h2>
                  <p className="mt-2.5 text-[15px] leading-relaxed text-ink">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · Our products */}
      <section id="products" className="relative scroll-mt-24 overflow-hidden bg-mesh py-20 lg:py-32" data-testid="home-products">
        <div aria-hidden className="absolute inset-0 bg-dots opacity-30" />
        <div className="relative mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal>
            <Eyebrow onDark>{s.productsEyebrow}</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-white lg:text-[2.5rem]">{s.productsTitle}</h2>
          </Reveal>

          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col rounded-[20px] glass-cell p-7 lg:p-8" data-testid="home-product-hms">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal font-display text-base font-black text-white">A</span>
                  <h3 className="font-display text-xl font-bold tracking-[-0.01em] text-white lg:text-2xl">Atikxzio HMS</h3>
                  <span className="rounded-full border border-teal/40 bg-teal/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-teal">{s.hmsFlag}</span>
                </div>
                <p className="mt-4 text-[17px] leading-relaxed text-slate-300">{s.hmsLine}</p>

                <div className="mt-7 rounded-2xl bg-navy-deep/60 p-3">
                  <ScreenShot
                    src="/screens/reception-home.webp"
                    alt="Atikxzio HMS reception home with today's bookings, OPD queue and free beds"
                    label="atikxzio.com/reception"
                    lang={lang}
                    caption={false}
                  />
                </div>
                <p className="mt-3 text-[11px] text-white/55">{SCREEN_CAPTION[lang]}</p>

                <div className="mt-auto flex flex-wrap gap-3 pt-7">
                  <a href={ATIKXZIO_SITE_URL} target="_top" rel="noopener" className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-navy transition-transform duration-200 hover:-translate-y-0.5" data-testid="home-product-hms-visit">
                    {s.hmsVisit} <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </a>
                  <Link to="/pricing" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="home-product-hms-pricing">
                    {s.hmsPricing}
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex h-full flex-col rounded-[20px] glass-cell p-7 lg:p-8" data-testid="home-product-cmh">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#C97B5E] font-display text-base font-black text-white">C</span>
                  <h3 className="font-display text-xl font-bold tracking-[-0.01em] text-white lg:text-2xl">{CMH_NAME}</h3>
                  <span className="rounded-full border border-[#E6B79E]/45 bg-[#C97B5E]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#F0CDB9]">{s.cmhFlag}</span>
                </div>
                <p className="mt-4 text-[17px] leading-relaxed text-slate-300">{s.cmhLine}</p>

                <div className="mt-7 overflow-hidden rounded-[20px]">
                  <CmhHeroVisual lang={lang} bare />
                </div>

                <div className="mt-auto pt-7">
                  <Link to="/products/atikxzio-cmh" className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-navy transition-transform duration-200 hover:-translate-y-0.5" data-testid="home-product-cmh-cta">
                    {s.cmhCta} <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5 · The patient journey */}
      <section className="bg-[#F6F8FC] py-20 lg:py-32" data-testid="home-journey">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Journey lang={lang} />
          <a href={ATIKXZIO_SITE_URL} target="_top" rel="noopener" className="group mt-12 inline-flex items-center gap-2 text-sm font-bold text-electric-ink" data-testid="home-journey-more">
            {s.journeyMore} <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </div>
      </section>

      {/* 6 · Numbers */}
      <section className="relative overflow-hidden bg-navy py-20 lg:py-28" data-testid="home-numbers">
        <div aria-hidden className="absolute -left-24 top-10 h-96 w-96 rounded-full bg-electric/20 blur-[140px]" />
        <div className="relative mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal><Eyebrow onDark>{s.numbersEyebrow}</Eyebrow></Reveal>
          <div className="mt-12 grid gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {s.numbers.map(([v, l], i) => (
              <Reveal key={l} delay={i * 0.07}>
                <div data-testid={`home-number-${i}`}>
                  <p className="font-display text-[2.75rem] font-black leading-none tracking-[-0.02em] text-white lg:text-[3.5rem]">{v}</p>
                  <p className="mt-3 max-w-[13rem] text-sm leading-relaxed text-slate-400">{l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7 · Leadership */}
      <section className="bg-[#F6F8FC] py-20 lg:py-32" data-testid="home-leadership">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Leadership lang={lang} />
        </div>
      </section>

      {/* 8 · Careers */}
      <section className="bg-navy py-16 lg:py-20" data-testid="home-careers-section">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-7 px-5 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <Eyebrow onDark>{s.careersEyebrow}</Eyebrow>
            <h2 className="mt-3 max-w-xl font-display text-[1.5rem] font-bold leading-snug tracking-[-0.01em] text-white lg:text-[1.875rem]">{s.careersTitle}</h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {s.careersRoles.map((r, i) => (
                <span key={r} className="rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 text-xs font-bold text-white/85" data-testid={`home-careers-role-${i}`}>{r}</span>
              ))}
            </div>
          </div>
          <Link to="/careers" className="group inline-flex w-fit items-center gap-2 rounded-full border border-white/35 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="home-careers-cta">
            {s.careersCta} <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* 9 · Final CTA */}
      <section className="bg-gradient-to-br from-electric-ink via-[#13529f] to-navy py-20 lg:py-28" data-testid="home-final-cta">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <h2 className="max-w-3xl font-display text-[1.875rem] font-black leading-tight tracking-[-0.02em] text-white sm:text-4xl lg:text-[3rem]">{s.ctaTitle}</h2>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-navy transition-transform duration-200 hover:-translate-y-0.5" data-testid="home-cta-demo">
              {s.ctaDemo} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="home-cta-contact">
              {s.ctaContact}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
